import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Download,
  RefreshCcw,
  Trash2,
  MessageCircle,
  Mail,
  Search,
  Inbox,
  ChevronRight,
  X,
  CheckCircle2,
  Clock,
  FileCheck2,
  ArchiveX,
} from "lucide-react";
import {
  apiMe,
  apiStats,
  apiListInquiries,
  apiUpdateStatus,
  apiDeleteInquiry,
  clearToken,
  exportCsvUrl,
  authHeader,
  formatApiError,
} from "../lib/api";

const STATUS_OPTIONS = [
  { value: "all", label: "All", icon: Inbox, color: "text-slate-600" },
  { value: "new", label: "New", icon: Clock, color: "text-emerald" },
  { value: "contacted", label: "Contacted", icon: MessageCircle, color: "text-blue-600" },
  { value: "quoted", label: "Quoted", icon: FileCheck2, color: "text-amber-600" },
  { value: "closed", label: "Closed", icon: ArchiveX, color: "text-slate-500" },
];

function StatusBadge({ status }) {
  const map = {
    new: "bg-emerald-50 text-emerald-700 border-emerald-200",
    contacted: "bg-blue-50 text-blue-700 border-blue-200",
    quoted: "bg-amber-50 text-amber-700 border-amber-200",
    closed: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${map[status] || map.new}`}>
      {status}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [items, s] = await Promise.all([apiListInquiries(filter), apiStats()]);
      setInquiries(items);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        nav("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load", { description: formatApiError(err) });
      }
    } finally {
      setBusy(false);
    }
  }, [filter, nav]);

  useEffect(() => {
    (async () => {
      try {
        const u = await apiMe();
        setMe(u);
      } catch {
        clearToken();
        nav("/admin/login", { replace: true });
      }
    })();
  }, [nav]);

  useEffect(() => {
    if (me) load();
  }, [me, load]);

  const logout = () => {
    clearToken();
    toast.success("Signed out");
    nav("/admin/login", { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      await apiUpdateStatus(id, status);
      toast.success("Status updated", { description: `Marked as ${status}.` });
      setInquiries((list) => list.map((it) => (it.id === id ? { ...it, status } : it)));
      if (selected?.id === id) setSelected({ ...selected, status });
      const s = await apiStats();
      setStats(s);
    } catch (err) {
      toast.error("Failed", { description: formatApiError(err) });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await apiDeleteInquiry(id);
      toast.success("Deleted");
      setInquiries((list) => list.filter((it) => it.id !== id));
      if (selected?.id === id) setSelected(null);
      const s = await apiStats();
      setStats(s);
    } catch (err) {
      toast.error("Failed to delete", { description: formatApiError(err) });
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(exportCsvUrl(), { headers: authHeader() });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] || "inquiries.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Export failed", { description: err.message });
    }
  };

  const filtered = inquiries.filter((it) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      it.company_name?.toLowerCase().includes(s) ||
      it.contact_person?.toLowerCase().includes(s) ||
      it.email?.toLowerCase().includes(s) ||
      it.country?.toLowerCase().includes(s) ||
      it.products_required?.toLowerCase().includes(s)
    );
  });

  if (!me) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-navy text-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/babul-logo.jpeg" alt="Babul" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/20" />
            <div>
              <div className="font-display font-bold leading-none">Babul Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-white/60">Inquiry Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={busy}
              data-testid="admin-refresh"
              className="p-2.5 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCcw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={downloadCsv}
              data-testid="admin-export-csv"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald hover:bg-[#004a35] text-sm font-semibold"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { l: "Total", v: stats?.total ?? "—", Icon: Inbox, accent: "bg-navy text-white" },
            { l: "New", v: stats?.new ?? "—", Icon: Clock, accent: "bg-emerald text-white" },
            { l: "Contacted", v: stats?.contacted ?? "—", Icon: MessageCircle, accent: "bg-blue-600 text-white" },
            { l: "Quoted", v: stats?.quoted ?? "—", Icon: FileCheck2, accent: "bg-amber-500 text-white" },
            { l: "Closed", v: stats?.closed ?? "—", Icon: ArchiveX, accent: "bg-slate-500 text-white" },
            { l: "Last 7 days", v: stats?.last_7d ?? "—", Icon: Clock, accent: "bg-gold text-navy" },
          ].map((s) => {
            const Icon = s.Icon;
            return (
              <div key={s.l} data-testid={`stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white rounded-2xl border border-slate-100 p-5">
                <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${s.accent} mb-3`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{s.l}</div>
                <div className="font-display text-2xl text-navy font-bold mt-1">{s.v}</div>
              </div>
            );
          })}
        </div>

        {/* Filters + search */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_OPTIONS.map((o) => {
              const Icon = o.icon;
              const active = filter === o.value;
              return (
                <button
                  key={o.value}
                  onClick={() => setFilter(o.value)}
                  data-testid={`filter-${o.value}`}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold border transition-all ${
                    active
                      ? "bg-navy text-white border-navy"
                      : "bg-white text-slate-600 border-slate-200 hover:border-navy"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {o.label}
                </button>
              );
            })}
          </div>
          <div className="md:ml-auto relative flex-1 md:max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search company, contact, country, product…"
              data-testid="admin-search"
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-slate-200 bg-[#F7F9FC] focus:outline-none focus:ring-2 focus:ring-emerald text-sm"
            />
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              {inquiries.length === 0
                ? "No inquiries yet. New submissions will appear here."
                : "No inquiries match your search."}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((it) => (
                <button
                  key={it.id}
                  onClick={() => setSelected(it)}
                  data-testid={`inquiry-row-${it.id}`}
                  className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors flex items-start gap-4"
                >
                  <div className="w-11 h-11 rounded-xl bg-emerald/10 text-emerald flex items-center justify-center font-bold shrink-0">
                    {(it.company_name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-navy truncate">{it.company_name}</span>
                      <StatusBadge status={it.status} />
                      <span className="text-xs text-slate-500">{fmtDate(it.created_at)}</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">
                      {it.contact_person} · {it.country} · {it.business_type}
                    </div>
                    <div className="text-sm text-slate-500 mt-1 line-clamp-1">
                      {it.products_required}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 mt-3 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-30"
            onClick={() => setSelected(null)}
            data-testid="detail-backdrop"
          />
          <aside className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-40 shadow-2xl overflow-y-auto" data-testid="inquiry-detail">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-emerald font-semibold">Inquiry Details</div>
                <div className="font-display text-xl text-navy font-bold">{selected.company_name}</div>
              </div>
              <button onClick={() => setSelected(null)} data-testid="detail-close" className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center gap-2">
                <StatusBadge status={selected.status} />
                <span className="text-xs text-slate-500">{fmtDate(selected.created_at)}</span>
              </div>

              {/* Quick actions */}
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={`https://wa.me/${(selected.whatsapp || selected.phone || "").replace(/[^\d]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="detail-whatsapp"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald text-white text-sm font-semibold hover:bg-[#004a35]"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                <a
                  href={`mailto:${selected.email}?subject=${encodeURIComponent("Re: Your export inquiry with Babul Corporation")}`}
                  data-testid="detail-email"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-[#0a1730]"
                >
                  <Mail className="w-4 h-4" /> Email
                </a>
              </div>

              {/* Status transitions */}
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Update Status</div>
                <div className="flex flex-wrap gap-2">
                  {["new", "contacted", "quoted", "closed"].map((st) => (
                    <button
                      key={st}
                      onClick={() => updateStatus(selected.id, st)}
                      data-testid={`set-status-${st}`}
                      disabled={selected.status === st}
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-all ${
                        selected.status === st
                          ? "bg-navy text-white border-navy cursor-default"
                          : "bg-white text-slate-600 border-slate-200 hover:border-navy hover:text-navy"
                      }`}
                    >
                      {st === selected.status && <CheckCircle2 className="inline w-3 h-3 mr-1 -mt-0.5" />}
                      {st.charAt(0).toUpperCase() + st.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <Detail label="Contact Person" value={selected.contact_person} />
              <Detail label="Country" value={selected.country} />
              <Detail label="Business Type" value={selected.business_type} />
              <Detail label="Email" value={selected.email} />
              <Detail label="Phone" value={selected.phone} />
              {selected.whatsapp && <Detail label="WhatsApp" value={selected.whatsapp} />}
              {selected.estimated_quantity && <Detail label="Estimated Quantity" value={selected.estimated_quantity} />}
              {selected.destination_port && <Detail label="Destination Port" value={selected.destination_port} />}
              <Detail label="Products Required" value={selected.products_required} multiline />
              {selected.message && <Detail label="Message" value={selected.message} multiline />}

              <div className="pt-4 border-t border-slate-100">
                <button
                  onClick={() => remove(selected.id)}
                  data-testid="detail-delete"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-red-600 border border-red-200 hover:bg-red-50 text-sm font-semibold"
                >
                  <Trash2 className="w-4 h-4" /> Delete inquiry
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

function Detail({ label, value, multiline }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{label}</div>
      <div className={`text-navy ${multiline ? "whitespace-pre-wrap" : ""}`}>{value}</div>
    </div>
  );
}
